import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3';
import { format } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList } from 'react-native';
import Toast, { useToast } from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import { CancelInviteMemberConfirmModal } from '@components/Modals/CancelInviteMemberConfirmModal.web';
import { RemoveMemberConfirmModal } from '@components/Modals/RemoveMemberConfirmModal';
import { WebModalV3 } from '@components/Modals/WebModalV3.web';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { MarginBottomM, MarginTopM } from '@components/shared/ListSpacers';
import {
  AssigneeRowSkeleton,
  SkeletonList,
} from '@components/shared/skeletons';
import { UpgradePaywallTooltip } from '@components/shared/UpgradePaywallTooltip';
import { BaseButtonV3 } from '@components/TaskList/BaseButtonV3';
import { DeleteFailedToast } from '@components/TaskList/DeleteFailedToast.web';
import { FileDeletedToast } from '@components/TaskList/FileDeletedToast.web';
import { SimpleHoverable } from '@components/TaskList/SimpleHoverable';
import {
  TaskInviteOrAssignee,
  UserSearchType,
  StringSearchType,
} from '@components/TaskList/TaskInviteOrAssignee.web';
import { TaskRoleSelectorWeb } from '@components/TaskList/TaskRoleSelector.web';
import {
  ChatDrawerScreen,
  useWebDrawer,
} from '@components/Web/Drawer/WebDrawerContext';
import {
  useGetTaskQuery,
  Task,
  TaskMemberRole,
  User,
  useCreateChatMutation,
  ProjectMemberRole,
  TeamMemberRole,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import { useHover } from '@hooks/useHover';
import useMe from '@hooks/useMe';
import { useTaskMemberMutations } from '@hooks/useTaskMemberMutations';
import { useTaskMembership } from '@hooks/useTaskMembership';
import { useAppNavigation } from '@navigation/useAppNavigation';
import {
  isStringSearchType,
  getDisplayName,
  getRoleDisplayString,
} from '@utils/taskMemberUtils';
import { shouldShowPaywallForProject } from '@utils/teams/paywall';

// Assignee rows keep their own local hover state (TA-9005): the row tints to
// grey01 on hover; controls inside deepen one step to grey02. Hover lives in
// this wrapper because rows come from a (hook-less) render function.
const HoverableAssigneeRow: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isHovered, hoverProps } = useHover();
  return (
    <Box
      {...hoverProps}
      flex={1}
      pl='m'
      pr='xs'
      py='m'
      borderColor='grey02'
      borderWidth={1}
      borderRadius='xs'
      flexDirection='row'
      backgroundColor={isHovered ? 'grey01' : 'white'}
      alignItems='center'>
      {children}
    </Box>
  );
};

const renderAssigneeSkeletonRow = (index: number) => (
  <AssigneeRowSkeleton key={index} />
);

interface TaskRoleSelectorPopModalProps {
  item: UserSearchType;
  isAssignee: boolean;
  isProjectEditor: boolean;
  isTeamMember: boolean;
  canEditTaskTopLevel: boolean;
  me: Pick<User, 'id'> | null | undefined;
  setRemoveTaskMemberData: React.Dispatch<
    React.SetStateAction<UserSearchType | undefined>
  >;
  setShowRemoveTaskMemberModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedUserList: React.Dispatch<React.SetStateAction<UserSearchType[]>>;
  updateTaskMember: (userId: User['id'], role: TaskMemberRole) => void;
}

// BaseButtonV3's `popModalComponent` is genuinely JSX-mounted, but this site
// lives inside `renderUser` below, a per-row render function with no hooks
// of its own (see the note above it) — a hook can't be called here, so this
// is a non-memoized factory call (S6478); it's a lateral move, not a
// regression, since the original inline arrow was also recreated every
// render.
const createTaskRoleSelectorPopModal =
  ({
    item,
    isAssignee,
    isProjectEditor,
    isTeamMember,
    canEditTaskTopLevel,
    me,
    setRemoveTaskMemberData,
    setShowRemoveTaskMemberModal,
    setSelectedUserList,
    updateTaskMember,
  }: TaskRoleSelectorPopModalProps) =>
  ({ onClose }: { onClose: () => void }) => (
    <TaskRoleSelectorWeb
      isAssignee={isAssignee}
      isProjectEditor={isProjectEditor}
      isTeamMember={isTeamMember}
      isOwner={canEditTaskTopLevel}
      showRemove={
        ((isAssignee && item.role === TaskMemberRole.Viewer) ||
          isProjectEditor ||
          isTeamMember ||
          canEditTaskTopLevel) &&
        item.id !== me?.id
      }
      onRemove={() => {
        setRemoveTaskMemberData(item);
        onClose();
        setShowRemoveTaskMemberModal(true);
      }}
      onRoleSelect={(role) => {
        setSelectedUserList((prev) => {
          if (prev.some((u) => u.id === item.id)) {
            return prev.map((u) => (u.id === item.id ? { ...u, role } : u));
          }

          const userWithRole: UserSearchType = {
            ...item,
            role: role,
          };

          return [...prev, userWithRole];
        });
        updateTaskMember(item.id as string, role);
        onClose();
      }}
      selectedRole={item.role}
    />
  );

// NOTE: Shared business logic (mutations, cache helpers, utils) extracted to
// @hooks/useTaskMemberMutations and @utils/taskMemberUtils.
// Remaining UI/rendering code is intentionally platform-specific.
export const TaskMembersNew = ({
  taskId = '',
  canEditTaskSecondLevel = false,
}: {
  taskId: string;
  canEditTaskSecondLevel: boolean;
}) => {
  const { t } = useTranslation();
  const toast = useToast();

  const { data: taskData, loading } = useGetTaskQuery({
    variables: {
      id: taskId,
    },
    skip: !taskId,
  });

  // getTask is null for a stale/deleted task (backend returns null since
  // TA-9862); coalesce to the role-null shape so the destructure below never
  // reads from null.
  // Cast bridges the GraphQL getTask selection type to the Task model.
  const task = (taskData?.getTask ?? {
    currentUserRole: null,
    currentUserProjectRole: null,
    currentUserProjectTeamRole: null,
  }) as Task;
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showRemoveTaskMemberModal, setShowRemoveTaskMemberModal] =
    useState(false);
  const [showCancelInviteTaskMemberModal, setShowCancelInviteTaskMemberModal] =
    useState(false);
  const [removeTaskMemberData, setRemoveTaskMemberData] =
    useState<UserSearchType>();
  const [isHovered, setIsHovered] = useState(false);

  const { members = [], invitations, userRequests } = task as Task;
  const { me } = useMe();
  const { setPaywallModalOpen, setPaywallTeam } = useWebDrawer();
  const [selectedUserList, setSelectedUserList] = useState<UserSearchType[]>(
    []
  );

  const [taskMembers, setTaskMembers] = useState<UserSearchType[]>([]);

  useEffect(() => {
    const map = new Map<string, UserSearchType>();
    for (const tm of members ?? []) {
      if (tm.role === TaskMemberRole.Owner) continue;

      const key = `user:${tm.user.id}`;
      const existing = map.get(key);

      const candidate: UserSearchType = {
        ...tm.user,
        role: tm.role,
        isTaskTagUser: false,
      };

      // Prefer Assignee over Viewer when duplicates exist.
      if (!existing) {
        map.set(key, candidate);
      } else if (
        existing.role === TaskMemberRole.Viewer &&
        tm.role === TaskMemberRole.Assignee
      ) {
        map.set(key, candidate);
      }
    }

    for (const tm of userRequests ?? []) {
      const key = `user:${tm.user?.id}`;
      const existing = map.get(key);

      const candidate: UserSearchType = {
        ...tm.user,
        role: tm.role,
        isTaskTagUser: true,
      };

      // Prefer Assignee over Viewer when duplicates exist.
      if (!existing) {
        map.set(key, candidate);
      } else if (
        existing.role === TaskMemberRole.Viewer &&
        tm.role === TaskMemberRole.Assignee
      ) {
        map.set(key, candidate);
      }
    }

    // Invitations
    for (const inv of invitations ?? []) {
      const key = `inv:${inv.email}`;
      const existing = map.get(key);

      const candidate: StringSearchType = {
        title: inv.email,
        role: inv.role,
        expiredAt: inv.expiredAt,
        id: inv.id,
        __typename: 'StringSearchType',
      };

      if (!existing) {
        map.set(key, candidate);
      } else if (
        existing.role === TaskMemberRole.Viewer &&
        inv.role === TaskMemberRole.Assignee
      ) {
        map.set(key, candidate);
      }
    }

    setSelectedUserList(Array.from(map.values()));
  }, [members, invitations, userRequests]);

  useEffect(() => {
    let members1: UserSearchType[] = [];
    members.map((member) => {
      members1.push({
        ...(member.user as User),
        role: member.role,
        isTaskTagUser: false,
      });
    });

    for (const member of userRequests ?? []) {
      members1.push({
        ...(member.user as User),
        role: member.role,
        userRequestId: member.id,
        isTaskTagUser: true,
      });
    }

    // Invitations
    for (const inv of invitations ?? []) {
      const invitation: StringSearchType = {
        title: inv.email,
        role: inv.role,
        expiredAt: inv.expiredAt,
        id: inv.id,
        __typename: 'StringSearchType',
      };
      members1.push(invitation);
    }
    members1.length > 0 && setTaskMembers(members1);
  }, [members, invitations, userRequests]);

  const data = useMemo(() => {
    const invitations = taskMembers.filter((t) => isStringSearchType(t));
    const tasktagUsers = taskMembers.filter((t) => t.isTaskTagUser);
    const members1 = taskMembers.filter(
      (t) => !t.isTaskTagUser && !isStringSearchType(t)
    );

    return [...members1, ...tasktagUsers, ...invitations];
  }, [taskMembers]);

  const { canEditTaskTopLevel, isAssignee } = useTaskMembership(task as Task);

  const isProjectEditor =
    task?.currentUserProjectRole === ProjectMemberRole.Editor;

  const isTeamMember =
    task?.currentUserProjectTeamRole === TeamMemberRole.Member;

  const {
    removeMemberFromTask: removeMemberFromTaskMutation,
    removeInviteMemberFromTask: removeInviteMemberFromTaskMutation,
    removeUserRequestFromTask: removeUserRequestFromTaskMutation,
    resendInviteMemberFromTask: resendInviteMemberFromTaskMutation,
    updateTaskMember: updateTaskMemberMutation,
  } = useTaskMemberMutations({ taskId, task });

  const toastRef = useRef(null);

  const webToastSuccess = (title: string, width = 240) => {
    toast.show(
      <FileDeletedToast title={title} duration={3000} width={width} />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: {
          backgroundColor: 'transparent',
          shadowColor: 'transparent',
          padding: 0,
          margin: 0,
          width,
          height: 80,
        },
      }
    );
  };

  const webToastError = (title: string, width = 320) => {
    toast.show(
      <DeleteFailedToast title={title} duration={3000} width={width} />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: {
          backgroundColor: 'transparent',
          shadowColor: 'transparent',
          padding: 0,
          margin: 0,
          width,
          height: 80,
        },
      }
    );
  };

  const resendInviteMemberFromTask = (member: UserSearchType) => {
    resendInviteMemberFromTaskMutation(member, {
      onError: () => {
        webToastError(t('models:tasks.resendInvite.resendFailed'));
      },
      onCompleted: () => {
        webToastSuccess(t('models:tasks.resendInvite.invitationResent'));
      },
    });
  };

  const removeInviteMemberFromTask = () => {
    if (!removeTaskMemberData) return;
    removeInviteMemberFromTaskMutation(removeTaskMemberData, {
      onCompleted: () => {
        setSelectedUserList((prev) =>
          prev.filter((t) => t.id !== removeTaskMemberData.id)
        );
        setTaskMembers((prev) =>
          prev.filter((t) => t.id !== removeTaskMemberData.id)
        );
        webToastSuccess(t('models:tasks.cancelInvite.inviteDeleted'));
      },
      onError: () => {
        webToastError(t('models:tasks.cancelInvite.deletedFailed'));
      },
    });
  };

  const removeUserRequestFromTask = () => {
    if (!removeTaskMemberData) return;
    removeUserRequestFromTaskMutation(removeTaskMemberData, {
      onCompleted: () => {
        setSelectedUserList((prev) =>
          prev.filter((t) => t.id !== removeTaskMemberData.id)
        );
        setTaskMembers((prev) =>
          prev.filter((t) => t.id !== removeTaskMemberData.id)
        );
        webToastSuccess(t('models:tasks.cancelInvite.inviteDeleted'));
      },
      onError: () => {
        webToastError(t('models:tasks.cancelInvite.deletedFailed'));
      },
    });
  };

  const removeMemberFromTask = (memberId: string) => {
    removeMemberFromTaskMutation(memberId, {
      onError: () => {
        webToastError(t('models:tasks.removeMembers.deletedFailed'));
      },
      onCompleted: () => {
        if (removeTaskMemberData) {
          setSelectedUserList((prev) =>
            prev.filter((t) => t.id !== removeTaskMemberData.id)
          );
          setTaskMembers((prev) =>
            prev.filter((t) => t.id !== removeTaskMemberData.id)
          );
        }
        webToastSuccess(
          removeTaskMemberData?.isTaskTagUser
            ? t('models:tasks.cancelInvite.inviteDeleted')
            : t('models:tasks.removeMembers.assigneeDeleted')
        );
      },
    });
  };

  const updateTaskMember = (userId: User['id'], role: TaskMemberRole) => {
    updateTaskMemberMutation(userId, role, {
      onError: () => {},
      onCompleted: () => {
        webToastSuccess(t('models:addMembers.roleUpdated'));
      },
    });
  };

  const { setActiveMessageId, setIsOpenChat } = useActiveChat();
  const { chatExpanded, chatListOpen, setChatListOpen, setShouldChatListOpen } =
    useWebDrawer();
  const { navigateToChatDrawer: navigateTo } = useAppNavigation();

  const [createChat, { data: createChatData }] = useCreateChatMutation();

  useEffect(() => {
    if (!createChatData) return;
    if (!chatExpanded && chatListOpen) {
      setShouldChatListOpen(true);
      setChatListOpen(false);
    }
    setIsOpenChat(true);
    setActiveMessageId?.(undefined);
    navigateTo({
      screen: ChatDrawerScreen.details,
      activeChat: createChatData.createChat,
    });
  }, [createChatData]);

  const openChat = ({ id }: UserSearchType) => {
    createChat({
      variables: {
        attributes: {
          userIds: [id],
        },
      },
    });
  };

  if (!task) {
    return (
      <Box flex={1} paddingTop='m' alignItems='center'>
        <Text>Task not found</Text>
      </Box>
    );
  }

  const overflowMapRef = useRef<Record<string, boolean>>({});
  const [, forceUpdate] = useState(0);

  const setOverflow = (id: string, overflow: boolean) => {
    if (overflowMapRef.current[id] === overflow) return;
    overflowMapRef.current[id] = overflow;
    forceUpdate((x) => x + 1);
  };

  const renderUser = (item: UserSearchType) => {
    const isTaskTagUser = item.isTaskTagUser ?? false;
    const isInvitaion = isStringSearchType(item);
    const displayName = getDisplayName(item);
    const { avatar, email } = item;
    const role = getRoleDisplayString(item.role);

    const isLongName = item.id && overflowMapRef.current[item.id];

    return (
      <HoverableAssigneeRow key={item.id}>
        {/* Left: render user / string item */}
        <Box flex={1} flexDirection='row' alignItems='center'>
          <TooltipWebV3
            minWidth='auto'
            marginOffset={0}
            title={
              <Text
                numberOfLines={0}
                flexShrink={1}
                variant='labelSmall'
                color='white'>
                {displayName}
              </Text>
            }
            placement='bottom-end'>
            <div style={{ flex: 1 }}>
              {isInvitaion ? (
                <Box
                  width={32}
                  height={32}
                  borderRadius='m'
                  backgroundColor='grey02'
                  alignItems='center'
                  justifyContent='center'>
                  <Icon
                    color='grey04'
                    variant='s'
                    name='User'
                    marginHorizontal='none'
                  />
                </Box>
              ) : (
                <Avatar
                  disableAvatarPress={true}
                  size='small'
                  id={item.id}
                  avatar={avatar}
                  label={displayName}
                  isMultiple={false}
                />
              )}
            </div>
          </TooltipWebV3>
          <Box ml='xs'>
            <TooltipWebV3
              minWidth='auto'
              marginOffset={0}
              disableHoverListener={!isLongName}
              disableFocusListener={!isLongName}
              disableTouchListener={!isLongName}
              title={
                <Text
                  numberOfLines={0}
                  flexShrink={1}
                  variant='labelSmall'
                  color='white'>
                  {displayName}
                </Text>
              }
              placement='bottom-end'>
              <div
                style={{
                  maxWidth: 230,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexShrink: 1,
                }}
                ref={(el) => {
                  if (!el) return;

                  requestAnimationFrame(() => {
                    const overflow = el.scrollWidth > el.clientWidth;
                    item.id && setOverflow(item.id, overflow);
                  });
                }}>
                <Text
                  variant='webSmall'
                  color='textPrimary'
                  flexShrink={1}
                  ellipsizeMode='tail'
                  numberOfLines={1}>
                  {displayName}
                </Text>
              </div>
            </TooltipWebV3>
            <Box maxWidth={230}>
              {email && (
                <Text
                  maxWidth={230}
                  flexShrink={1}
                  variant='webBodyHintSubline'
                  color='grey04'
                  marginTop='xxs'
                  ellipsizeMode='tail'
                  numberOfLines={1}>
                  {email}
                </Text>
              )}
              {isInvitaion && item.expiredAt && (
                <Text
                  maxWidth={230}
                  variant='webBodyHintSubline'
                  color={
                    new Date(item.expiredAt) < new Date()
                      ? 'alertRed'
                      : 'grey04'
                  }
                  flexShrink={1}
                  marginTop='xxs'
                  ellipsizeMode='tail'
                  numberOfLines={1}>
                  {'Invite expired ' + format(item.expiredAt, 'MMM dd, yyyy')}
                </Text>
              )}
            </Box>
          </Box>
        </Box>

        {!isTaskTagUser && !isInvitaion && (
          <TooltipWebV3
            minWidth='auto'
            marginOffset={0}
            title={
              <Text
                numberOfLines={0}
                flexShrink={1}
                variant='labelSmall'
                color='white'>
                Send Message
              </Text>
            }
            placement='bottom-end'>
            <div style={{ flex: 1 }}>
              <SimpleHoverable onPress={() => openChat(item)}>
                {(hover) => (
                  <Box
                    padding='xs'
                    borderRadius='xs'
                    backgroundColor={hover ? 'grey02' : undefined}
                    alignItems='center'
                    justifyContent='center'>
                    <Icon
                      color='grey05'
                      variant='s'
                      name='MessageSquare'
                      marginHorizontal='none'
                    />
                  </Box>
                )}
              </SimpleHoverable>
            </div>
          </TooltipWebV3>
        )}

        {isInvitaion && canEditTaskSecondLevel && (
          <TooltipWebV3
            minWidth='auto'
            marginOffset={0}
            title={
              <Text
                numberOfLines={0}
                flexShrink={1}
                variant='labelSmall'
                color='white'>
                Resend Invitation
              </Text>
            }
            placement='bottom-end'>
            <div style={{ flex: 1 }}>
              <SimpleHoverable
                onPress={() => {
                  resendInviteMemberFromTask(item);
                }}>
                {(hover) => (
                  <Box
                    padding='xs'
                    borderRadius='xs'
                    backgroundColor={hover ? 'grey02' : undefined}
                    alignItems='center'
                    justifyContent='center'
                    marginRight={
                      !(isAssignee && item.role === TaskMemberRole.Assignee)
                        ? 'xs'
                        : undefined
                    }>
                    <Icon
                      color='grey05'
                      variant='s'
                      name='Repeat'
                      marginHorizontal='none'
                    />
                  </Box>
                )}
              </SimpleHoverable>
            </div>
          </TooltipWebV3>
        )}

        {(isTaskTagUser || isInvitaion) &&
          canEditTaskSecondLevel &&
          me?.id !== item.id &&
          !(isAssignee && item.role === TaskMemberRole.Assignee) && (
            <TooltipWebV3
              minWidth='auto'
              marginOffset={0}
              title={
                <Text
                  numberOfLines={0}
                  flexShrink={1}
                  variant='labelSmall'
                  color='white'>
                  Remove Invitation
                </Text>
              }
              placement='bottom-end'>
              <div style={{ flex: 1 }}>
                <SimpleHoverable
                  onPress={() => {
                    setRemoveTaskMemberData(item);
                    setShowCancelInviteTaskMemberModal(true);
                  }}>
                  {(hover) => (
                    <Box
                      padding='xs'
                      borderRadius='xs'
                      backgroundColor={hover ? 'grey02' : undefined}
                      alignItems='center'
                      justifyContent='center'>
                      <Icon
                        color={hover ? 'alertRed' : 'grey05'}
                        variant='s'
                        name='Trash2'
                        marginHorizontal='none'
                      />
                    </Box>
                  )}
                </SimpleHoverable>
              </div>
            </TooltipWebV3>
          )}

        {(item.role === TaskMemberRole.Owner || !canEditTaskSecondLevel) && (
          <Box
            width={86}
            ml='l'
            mr='m'
            alignItems='center'
            justifyContent='flex-end'
            flexDirection='row'>
            <Text variant='webSmall' color='textPrimary'>
              {role}
            </Text>
          </Box>
        )}

        {(isTaskTagUser || isInvitaion) && canEditTaskSecondLevel && (
          <Box
            width={86}
            ml='l'
            mr='m'
            alignItems='center'
            justifyContent='flex-end'
            flexDirection='row'>
            <Text variant='webSmall' color='grey04' marginRight='xs'>
              {role}
            </Text>
            <Icon color='grey04' variant='s' name='ChevronDown' />
          </Box>
        )}

        {/* Right: role selector for User only */}
        {item.role !== TaskMemberRole.Owner &&
          !isTaskTagUser &&
          !isInvitaion &&
          (isProjectEditor || isTeamMember || canEditTaskSecondLevel) && (
            <>
              {item.id === me?.id ||
              (isAssignee &&
                item.role === TaskMemberRole.Assignee &&
                !canEditTaskTopLevel) ? (
                <Box
                  width={86}
                  ml='l'
                  mr='m'
                  alignItems='center'
                  justifyContent='flex-end'
                  flexDirection='row'>
                  <Text variant='webSmall' color='textPrimary'>
                    {item.id === me?.id
                      ? getRoleDisplayString(TaskMemberRole.Assignee)
                      : role}
                  </Text>
                </Box>
              ) : (
                <Box ml='xs'>
                  <BaseButtonV3
                    noBorder={true}
                    noRadius={false}
                    noHover={false}
                    buttonRadius='xs'
                    modalRadius={8}
                    showHoverWhenDropdown={true}
                    hoverBackgroundColor='grey02'
                    restingBackgroundColor='transparent'
                    popModalComponent={createTaskRoleSelectorPopModal({
                      item,
                      isAssignee,
                      isProjectEditor,
                      isTeamMember,
                      canEditTaskTopLevel,
                      me,
                      setRemoveTaskMemberData,
                      setShowRemoveTaskMemberModal,
                      setSelectedUserList,
                      updateTaskMember,
                    })}>
                    <Box
                      width={86}
                      mx='m'
                      alignItems='center'
                      justifyContent='flex-end'
                      flexDirection='row'>
                      <Text
                        variant='webSmall'
                        color='textPrimary'
                        marginRight='xs'>
                        {role}
                      </Text>
                      <Icon color='grey05' variant='s' name='ChevronDown' />
                    </Box>
                  </BaseButtonV3>
                </Box>
              )}
            </>
          )}
      </HoverableAssigneeRow>
    );
  };

  const addMembers = () => {
    // Check paywall before opening add members modal
    const taskWithProject = task as Task;
    if (
      taskWithProject?.project &&
      shouldShowPaywallForProject(taskWithProject.project)
    ) {
      setPaywallTeam(taskWithProject.project?.team ?? undefined);
      setPaywallModalOpen(true);
      return;
    }
    setShowAddMembersModal(true);
  };

  // useGetTaskQuery defaults to cache-and-network (useApolloClient.tsx),
  // so `loading` can be true again during a background revalidation even
  // though `data` already has rows from cache — gate on both so a refetch
  // never wipes real assignee rows back to skeletons.
  const isInitialAssigneeLoading = loading && data.length === 0;

  return (
    <Box flex={1} backgroundColor='white'>
      {isInitialAssigneeLoading ? (
        <Box mb='listFooter'>
          <SkeletonList row={renderAssigneeSkeletonRow} />
        </Box>
      ) : (
        <FlatList
          data={data}
          renderItem={({ item }) => renderUser(item)}
          keyExtractor={(item) => (item.id ? item.id.toString() : '')}
          ItemSeparatorComponent={MarginTopM}
          ListFooterComponent={MarginBottomM}
          ListHeaderComponent={MarginTopM}
        />
      )}

      {!loading && canEditTaskSecondLevel && (
        <UpgradePaywallTooltip
          show={
            !!(task as Task)?.project &&
            shouldShowPaywallForProject((task as Task).project)
          }>
          <SimpleHoverable onPress={() => addMembers()}>
            {() => (
              <Box
                px='l'
                py='m'
                flex={1}
                borderRadius='xs'
                flexDirection='row'
                alignItems='center'
                backgroundColor={isHovered ? 'grey02' : 'grey01'}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}>
                <Icon variant='s' name='Plus' color='greenSecondary' />
                <Text variant='webSmall' marginLeft='s' color='greenSecondary'>
                  {t('shared:assignee')}
                </Text>
              </Box>
            )}
          </SimpleHoverable>
        </UpgradePaywallTooltip>
      )}

      {showAddMembersModal && (
        <WebModalV3
          canOverflowModal={true}
          accessibilityLabel='Assign Task Member'
          onClose={() => {
            setShowAddMembersModal(false);
          }}
          visible={showAddMembersModal}
          width={800}
          height={412}>
          <TaskInviteOrAssignee
            isProjectEditor={isProjectEditor}
            isTeamMember={isTeamMember}
            selectedUser={selectedUserList}
            taskIdList={[taskId]}
            onClose={() => {
              setShowAddMembersModal(false);
            }}
          />
        </WebModalV3>
      )}
      {/* Remove task member confirmation modal */}
      {showRemoveTaskMemberModal && removeTaskMemberData && (
        <RemoveMemberConfirmModal
          showModal={showRemoveTaskMemberModal}
          onClose={() => setShowRemoveTaskMemberModal(false)}
          onPress={() => {
            removeTaskMemberData.id &&
              removeMemberFromTask(removeTaskMemberData.id);
            setShowRemoveTaskMemberModal(false);
          }}
          buttonText={t('models:tasks.removeMembers.remove')}
          title={t('models:tasks.removeMembers.title')}
          message={t('models:tasks.removeMembers.removeTaskMemberMessage')}
          memberName={
            isStringSearchType(removeTaskMemberData)
              ? removeTaskMemberData.title
              : (removeTaskMemberData.name ?? '')
          }
          isAlert
        />
      )}
      {/* Cancel invite confirmation modal */}
      {showCancelInviteTaskMemberModal && removeTaskMemberData && (
        <CancelInviteMemberConfirmModal
          showModal={showCancelInviteTaskMemberModal}
          onClose={() => setShowCancelInviteTaskMemberModal(false)}
          onPress={() => {
            setShowCancelInviteTaskMemberModal(false);
            if (removeTaskMemberData.id) {
              if (isStringSearchType(removeTaskMemberData)) {
                removeInviteMemberFromTask();
              } else if (removeTaskMemberData.isTaskTagUser) {
                removeUserRequestFromTask();
              } else {
                removeMemberFromTask(removeTaskMemberData.id);
              }
            }
          }}
          buttonText={t('models:tasks.cancelInvite.cancelInvite')}
          title={t('models:tasks.cancelInvite.title')}
          message={t('models:tasks.cancelInvite.cancelInviteTaskMemberMessage')}
          memberName={
            isStringSearchType(removeTaskMemberData)
              ? removeTaskMemberData.title
              : (removeTaskMemberData.name ?? '')
          }
          isAlert
        />
      )}
      <Toast ref={toastRef} />
    </Box>
  );
};
